/* global enrollmentDeadline */
// BOOTSTRAP 5.3.8
import '../../source/vendor/bootstrap-5.3.8/dist/js/bootstrap.bundle.min.js';

const HOMEPAGE_STATES = {
    enrollment: 'enrollment',
    closed: 'closed'
};

const HOMEPAGE_HEADLINES = {
    [HOMEPAGE_STATES.enrollment]: 'Enrollment ends in',
    [HOMEPAGE_STATES.closed]: 'Enrollment has ended'
};

/**
 * Parse a date-only string as a local browser date to avoid timezone drift.
 *
 * @param {string} dateString
 * @param {boolean} endOfDay
 * @returns {Date|null}
 */
function parseLocalDate(dateString, endOfDay = false) {
    if (typeof dateString !== 'string' || dateString.trim() === '') {
        return null;
    }

    const match = dateString.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (!match) {
        console.error('Invalid local date:', dateString);
        return null;
    }

    // Skip the first match which is the full string, and destructure year, month, and day
    const [, yearString, monthString, dayString] = match;
    const year = Number(yearString);
    const month = Number(monthString) - 1;
    const day = Number(dayString);

    if (endOfDay) {
        // Auto set to 11:59:59.999 PM local time to ensure the entire enrollment deadline day is included in the countdown
        return new Date(year, month, day, 23, 59, 59, 999);
    }

    return new Date(year, month, day, 12, 0, 0, 0);
}

/**
 * Format the enrollment deadline without timezone drift.
 *
 * @param {string} dateString
 * @returns {string}
 */
function formatEnrollmentDeadline(dateString) {
    const enrollmentDate = parseLocalDate(dateString);

    if (!enrollmentDate) {
        return dateString;
    }

    return enrollmentDate.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
}

/**
 * Determine which homepage state should be rendered for the current time.
 *
 * @param {Date} now
 * @param {Date|null} enrollmentDeadlineDate
 * @returns {string}
 */
function getHomepageState(now, enrollmentDeadlineDate) {
    if (enrollmentDeadlineDate && now <= enrollmentDeadlineDate) {
        return HOMEPAGE_STATES.enrollment;
    }

    return HOMEPAGE_STATES.closed;
}

/**
 * Get the countdown target for the current homepage state.
 *
 * @param {string} state
 * @param {Date|null} enrollmentDeadlineDate
 * @returns {Date|null}
 */
function getCountdownTarget(state, enrollmentDeadlineDate) {
    if (state === HOMEPAGE_STATES.enrollment) {
        return enrollmentDeadlineDate;
    }

    return null;
}

/**
 * Update enrollment date copy wherever it appears on the page.
 *
 * @param {NodeListOf<HTMLElement>} enrollmentDateElements
 * @param {string} dateString
 * @returns {void}
 */
function updateEnrollmentDateText(enrollmentDateElements, dateString) {
    if (enrollmentDateElements.length === 0) {
        return;
    }

    const formattedDate = formatEnrollmentDeadline(dateString);

    enrollmentDateElements.forEach((element) => {
        element.style.padding = '0';
        element.textContent = formattedDate;
    });
}

/**
 * Update the homepage headline for the current state.
 *
 * @param {HTMLElement|null} headlineElement
 * @param {string} state
 * @returns {void}
 */
function updateCountdownHeadline(headlineElement, state) {
    if (!headlineElement) {
        return;
    }

    headlineElement.textContent = HOMEPAGE_HEADLINES[state];
}

/**
 * Hide the enroll CTA once the enrollment deadline has passed.
 *
 * @param {NodeListOf<HTMLElement>} enrollNowElements
 * @param {boolean} shouldHide
 * @returns {void}
 */
function updateEnrollNowVisibility(enrollNowElements, shouldHide) {
    if (enrollNowElements.length === 0) {
        return;
    }

    enrollNowElements.forEach((element) => {
        element.hidden = shouldHide;
    });
}

/**
 * Hide the header notice enrollment copy once the enrollment deadline has passed.
 *
 * @param {HTMLElement|null} headerNoticeElement
 * @param {boolean} shouldHide
 * @returns {void}
 */
function updateHeaderNoticeVisibility(headerNoticeElement, shouldHide) {
    if (!headerNoticeElement) {
        return;
    }

    headerNoticeElement.hidden = shouldHide;
}

/**
 * Write countdown values to the page.
 *
 * @param {{days: HTMLElement|null, hours: HTMLElement|null, minutes: HTMLElement|null, seconds: HTMLElement|null}} countdownElements
 * @param {Date|null} targetDate
 * @param {Date} now
 * @returns {void}
 */
function updateCountdownValues(countdownElements, targetDate, now) {
    const values = Object.values(countdownElements);

    if (values.some((element) => !element)) {
        return;
    }

    let timeDiff = 0;

    if (targetDate) {
        timeDiff = Math.max(targetDate.getTime() - now.getTime(), 0);
    }

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

    countdownElements.days.textContent = days.toString().padStart(2, '0');
    countdownElements.hours.textContent = hours.toString().padStart(2, '0');
    countdownElements.minutes.textContent = minutes.toString().padStart(2, '0');
    countdownElements.seconds.textContent = seconds.toString().padStart(2, '0');
}

/**
 * Render homepage deadline-driven UI from a single state controller.
 *
 * @returns {void}
 */
function initializeHomepageDateState() {
    const enrollmentDateElements = document.querySelectorAll('.js-enrollment-date mark');
    const enrollNowElements = document.querySelectorAll('.link-enroll-now');
    const headerNoticeElement = document.querySelector('.header-notice > .js-enrollment-date');
    const headlineElement = document.getElementById('countdown-headline');
    const countdownElements = {
        days: document.getElementById('days'),
        hours: document.getElementById('hours'),
        minutes: document.getElementById('minutes'),
        seconds: document.getElementById('seconds')
    };
    const enrollmentDeadlineDate = parseLocalDate(enrollmentDeadline, true);

    updateEnrollmentDateText(enrollmentDateElements, enrollmentDeadline);

    if (!headlineElement && enrollNowElements.length === 0 && enrollmentDateElements.length === 0) {
        return;
    }

    let intervalId = null;

    const render = () => {
        const now = new Date();
        const state = getHomepageState(now, enrollmentDeadlineDate);
        const countdownTarget = getCountdownTarget(state, enrollmentDeadlineDate);

        updateCountdownHeadline(headlineElement, state);
        updateEnrollNowVisibility(enrollNowElements, state !== HOMEPAGE_STATES.enrollment);
        updateHeaderNoticeVisibility(headerNoticeElement, state !== HOMEPAGE_STATES.enrollment);
        updateCountdownValues(countdownElements, countdownTarget, now);

        if (state === HOMEPAGE_STATES.closed && intervalId) {
            window.clearInterval(intervalId);
            intervalId = null;
        }

        return state;
    };

    const initialState = render();

    if (initialState === HOMEPAGE_STATES.enrollment) {
        intervalId = window.setInterval(render, 1000);
    }
}

function getReportDataByIndex(index = 'totals') {
    // Get the data from the WordPress API endpoint /wp-json/tsuite/v1/reports/summary?year=2026
    return fetch('/wp-json/tsuite/v1/reports/summary?year=2026', { cache: 'no-store' })
        .then((response) => response.json())
        .then((data) => {
            if (data) {
                return data[index];
            } else {
                throw new Error('Data not found for index: ' + index);
            }
        })
        .catch((error) => {
            console.error('Error fetching report data:', error);
            return null;
        });
}

function updateDataPointsOnPage() {
    // js-enrollment-dealers = forms
    // js-enrollment-students = backpacks
    // js-enrollment-progress = amount raised
    const elementsToUpdate = document.querySelectorAll(
        '.js-enrollment-dealers, .js-enrollment-students, .js-enrollment-progress'
    );
    if (elementsToUpdate.length > 0) {
        getReportDataByIndex('totals').then((reportData) => {
            if (reportData) {
                // Get percentage of dealers enrolled based on forms submitted, with a fallback to 0% if there are no forms submitted to avoid division by zero
                // let enrollmentProgressData = 0;
                // if (reportData.forms && reportData.dealerCount > 0) {
                //     enrollmentProgressData = (reportData.forms / reportData.dealerCount) * 100;
                // }
                const enrolledForms = reportData.forms || 0;
                const backpackCount = reportData.backpacksAdjusted || reportData.backpacks_adjusted || reportData.backpacksWithMatch || reportData.backpacks_with_match || reportData.backpacks || 0;
                // const enrollmentProgress = enrollmentProgressData.toFixed(2);
                const totalRaised = reportData.amountAdjusted || reportData.amount_adjusted || reportData.amountWithMatch || reportData.amount_with_match || reportData.amount || 0;

                document.querySelectorAll('.js-enrollment-dealers').forEach((element) => {
                    element.textContent = enrolledForms.toLocaleString();
                });

                document.querySelectorAll('.js-enrollment-students').forEach((element) => {
                    element.textContent = backpackCount.toLocaleString();
                });

                // Render as US Currency with no decimal places, and a fallback to $0 if there is no amount to avoid displaying NaN
                document.querySelectorAll('.js-enrollment-progress').forEach((element) => {
                    element.textContent = totalRaised.toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                    });
                });
            }
        });
    }
}

/**
 * Adjusts region chart percentage label position when columns are too short.
 *
 * If a column is 54px or less in height, the percentage label is moved above it using
 * a negative top margin. Otherwise, the percentage label margin is reset.
 *
 * @returns {void}
 */
function updateRegionChartPercentagePosition() {
    const regionChartColumns = document.querySelectorAll('button.js-pacecore-region-chart-bar, .pacecore-region-chart__bar');

    if (regionChartColumns.length === 0) {
        return;
    }

    regionChartColumns.forEach((column) => {
        const percentageLabel = column.querySelector('.pacecore-region-chart__percent-text');

        if (!percentageLabel) {
            return;
        }

        const columnHeight = column.getBoundingClientRect().height;

        if (columnHeight <= 54) {
            percentageLabel.style.marginTop = '-50px';
        } else {
            percentageLabel.style.marginTop = '';
        }
    });
}

document.addEventListener('DOMContentLoaded', function () {
    initializeHomepageDateState();

    document.getElementById('hamburger-menu').addEventListener('click', function () {
        document.querySelector('.hamburger-menu').classList.toggle('hamburger-menu-open');
        document.querySelector('#menu-main-navigation.menu').classList.toggle('mobile-nav-open');
    });

    const splideSlides = document.querySelectorAll('.splide__slide');
    splideSlides.forEach((slide) => {
        slide.removeAttribute('role');
    });

    updateDataPointsOnPage();

    updateRegionChartPercentagePosition();

    window.addEventListener('resize', updateRegionChartPercentagePosition);
});
